<script lang="ts" setup>
import {
  type DocumentSpaceLink,
  useCreateDocumentSpaceLinkMappingMutation,
  useDeleteDocumentSpaceLinkMappingMutation,
} from "@/graphql/operations";
import { Tooltip } from "@/components";
import { PropType, ref, computed } from "vue";

const props = defineProps({
  link: {
    type: Object as PropType<DocumentSpaceLink>,
    required: true,
  },
  documentSpaceId: {
    type: String,
    required: true,
  },
});

const emits = defineEmits(["success"]);

const displayTranslations = ref<boolean>(false);

const { executeMutation, fetching: creating } =
  useCreateDocumentSpaceLinkMappingMutation();
const { executeMutation: executeDelete, fetching: deleting } =
  useDeleteDocumentSpaceLinkMappingMutation();
const saving = computed(() => creating.value || deleting.value);
const saveError = ref(false);
const handleChange = async (event: Event) => {
  const mapping = props.link.documentSpaceLinkMappingsByLinkId.nodes?.[0];
  // The browser toggles before change; retain the stored state until confirmed.
  (event.currentTarget as HTMLInputElement).checked = !!mapping;
  if (saving.value) return;
  saveError.value = false;
  const result = mapping
    ? await executeDelete({ mappingId: mapping.id })
    : await executeMutation({
        linkId: props.link.id,
        spaceId: props.documentSpaceId,
      });
  if (
    result.error ||
    !(
      result.data &&
      (("deleteDocumentSpaceLinkMapping" in result.data &&
        result.data.deleteDocumentSpaceLinkMapping) ||
        ("createDocumentSpaceLinkMapping" in result.data &&
          result.data.createDocumentSpaceLinkMapping))
    )
  ) {
    saveError.value = true;
    return;
  }
  emits("success");
};
</script>

<template>
  <div class="row">
    <Tooltip v-if="link.isMandatory" class="left">
      <template v-slot>
        <input type="checkbox" :checked="true" :disabled="true" />
      </template>
      <template #tooltip>{{
        $t("documentSpace.configLinks.mandatoryLink.info")
      }}</template>
    </Tooltip>

    <input
      v-else
      type="checkbox"
      :disabled="saving"
      @change="handleChange"
      :checked="link.documentSpaceLinkMappingsByLinkId.nodes?.length > 0"
    />
    <div class="cell stretch center">
      <b>{{ link.sectionTitle }}</b>
      <button
        v-if="link.documentSpaceLinkTranslationsByLinkId.nodes.length > 0"
        type="button"
        class="icon_button material-symbols-outlined clickable"
        data-test="toggle-config-link-translations"
        :aria-label="$t('documentSpace.configLinks.title')"
        :aria-expanded="displayTranslations"
        @click="displayTranslations = !displayTranslations"
      >
        <template v-if="displayTranslations"> expand_less </template>
        <template v-else>expand_more</template>
      </button>
    </div>
    <div class="cell stretch center">
      <div>
        <a target="__blank" :href="link.url">{{ link.title }}</a>
      </div>
    </div>
    <div class="cell center icon_container">
      <Tooltip v-if="link.isMandatory" class="right">
        <template v-slot>
          <span class="material-symbols-outlined">lock</span>
        </template>
        <template #tooltip>{{
          $t("documentSpace.configLinks.mandatoryLink.info")
        }}</template>
      </Tooltip>
    </div>
  </div>
  <p v-if="saveError" role="alert" class="save_error">{{ $t("error") }}</p>
  <template v-if="displayTranslations">
    <div
      v-for="translation in link.documentSpaceLinkTranslationsByLinkId.nodes"
      :key="translation!.id"
      class="row inner"
    >
      <div class="cell stretch center">
        {{ translation!.languageCode }}:
        {{ translation!.sectionTitle }}
      </div>
      <div class="cell stretch center">
        <div>
          <a target="__blank" :href="translation!.url">{{
            translation!.title
          }}</a>
        </div>
      </div>
      <div class="cell center icon_container"></div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.save_error {
  color: var(--classic-danger, #c62828);
  font-size: 0.9em;
}
.row {
  border-top: 1px solid var(--classic-border, #dbdbdb);
  border-bottom: 1px solid var(--classic-surface, #fff);
  background: var(--classic-surface, #fff);
  width: 100%;
  display: flex;
  padding: 0.5em 0;
  font-size: 0.9rem;
  align-items: center;
  box-sizing: border-box;
}

.row + .row.inner {
  border-top: none;
}

.row.inner {
  padding-left: 22px;
}

.row:last-child {
  border-bottom: 1px solid var(--classic-border, #dbdbdb);
}

.cell {
  padding: 0 0.5em;
  line-height: 2.1em;
}

.cell.stretch {
  flex: 1;
}

.cell.grow {
  flex-grow: 1;
}

.cell.center {
  align-self: center;
}

.material-symbols-outlined {
  font-size: 1.2em;
  vertical-align: middle;
}

.icon_button {
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
}

.cell a {
  color: var(--classic-accent, #444);
  text-decoration: none;
  cursor: pointer;
}

.icon_container {
  width: 30px;
  text-align: right;
}

input[type="checkbox"]:disabled {
  opacity: 0.4;
}

b {
  font-weight: 500;
}
</style>
